/* ===========================================================================
   c-cart-drawer.js — enhancements for the SL cart drawer (Step Labs)

   Everything here is progressive: the subscription switch and the pairing cards
   are real <form>s that post to /cart/change and /cart/add, so they still work
   when this file never runs (see AGENTS.md: `global-script.js` disables scripts
   for desktop-Linux visitors). With JS we stay in the drawer instead:
     - subscription switch / cancel → /cart/change.js with `selling_plan`
     - "Add to Cart" on a pairing card → /cart/add.js
     - "Add to Cart" on the page (PDP hero, Similar Models, collection cards)
       → /cart/add.js, then this drawer opens instead of leaving for /cart
     - related-products fallback when no complementary products are configured
     - free gift: add it when the cart qualifies, remove it when it no longer does
   After each change we fire `on:cart:change`, which Symmetry's `cart-form`
   listens to and re-renders the section with. The DOM inside the drawer is
   replaced on every refresh, so all listeners are delegated from `document`.
   =========================================================================== */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const refreshCart = () => {
    document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
  };

  const post = (path, body) => fetch(`${root}${path}`, {
    method: 'POST',
    headers: jsonHeaders,
    body: JSON.stringify(body)
  }).then((response) => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  });

  /* ---------- Subscription switch / cancel ---------- */
  document.addEventListener('submit', (evt) => {
    const form = evt.target.closest('.c-cart form[data-c-cart-plan]');
    if (!form) return;
    evt.preventDefault();

    const data = new FormData(form);
    const plan = data.get('selling_plan');
    const switchBtn = form.querySelector('[role="switch"]');
    if (switchBtn) switchBtn.setAttribute('aria-checked', 'true');
    form.classList.add('is-busy');

    post('cart/change.js', {
      id: data.get('id'),
      quantity: Number(data.get('quantity')) || 1,
      selling_plan: plan ? Number(plan) : null
    })
      .then(refreshCart)
      .catch(() => form.submit());
  });

  /* ---------- "Add to Cart" on pairing cards ---------- */
  document.addEventListener('submit', (evt) => {
    const form = evt.target.closest('.c-cart form[data-c-cart-add]');
    if (!form) return;
    evt.preventDefault();

    const btn = form.querySelector('button');
    if (btn) btn.setAttribute('aria-busy', 'true');
    const data = new FormData(form);

    post('cart/add.js', { items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity')) || 1 }] })
      .then(refreshCart)
      .catch(() => {
        if (btn) btn.removeAttribute('aria-busy');
      });
  });

  /* ---------- "Add to Cart" anywhere on the page opens this drawer ----------
     The c-pdp hero (`#c-pdp-form`, which its mobile sticky bar also submits)
     and the forms marked `data-c-atc` (Similar Models, collection cards) are
     plain product forms outside Symmetry's <product-form>, so on their own
     they post to /cart/add and leave the page for /cart. Here they add through
     /cart/add.js and the drawer opens on the add itself — the "Adds made by
     another app" watcher below sees the request and refreshes + opens it.

     `defaultPrevented` first: an app that owns the button (Candy Rack's
     upsell pop-up) or another theme handler may already have taken this
     submit, and adding a second time would double the line. Forms marked
     `data-atc` are NOT matched — c-lp-cart.js and the inline handlers in
     c-home-products / c-pdp-hero own those. */
  document.addEventListener('submit', (evt) => {
    if (evt.defaultPrevented) return;
    const form = evt.target.closest('form#c-pdp-form, form[data-c-atc]');
    if (!form || !document.querySelector('.c-cart')) return;
    evt.preventDefault();

    const btn = evt.submitter || form.querySelector('[type="submit"]');
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }

    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form)
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
      })
      .catch(() => form.submit()) // e.g. out of stock: Shopify's own /cart/add explains it
      .finally(() => {
        if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
      });
  });

  /* ---------- Related-products fallback ---------- */
  const loadRecommendations = (body) => {
    const block = body.querySelector('[data-c-cart-recs]');
    if (!block || block.dataset.loaded) return;
    block.dataset.loaded = 'true';

    fetch(block.dataset.cCartRecs)
      .then((response) => (response.ok ? response.text() : ''))
      .then((html) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const result = doc.querySelector('[data-c-cart-recs-result]');
        const target = block.querySelector('[data-c-cart-recs-target]');
        if (!result || !target || !result.querySelector('.c-cart-pair')) return;
        target.innerHTML = result.innerHTML;
        block.hidden = false;
      })
      .catch(() => {});
  };

  /* ---------- Free gift ---------- */
  let giftPending = null;

  const reconcileGift = (body) => {
    const variantId = Number(body.dataset.giftVariant);
    if (!variantId) return;

    const eligible = body.dataset.giftEligible === 'true';
    const count = Number(body.dataset.giftCount) || 0;
    let action = null;
    if (eligible && count === 0) action = 'add';
    if (!eligible && count > 0) action = 'remove';
    if (!action || giftPending === action) return;
    giftPending = action;

    let request;
    if (action === 'add') {
      request = post('cart/add.js', {
        items: [{ id: variantId, quantity: 1, properties: { _c_gift: '1' } }]
      });
    } else {
      const updates = {};
      body.querySelectorAll('.c-cart-line--gift .cart-item__quantity-input').forEach((input) => {
        updates[input.dataset.key] = 0;
      });
      request = post('cart/update.js', { updates });
    }

    request
      .then(refreshCart)
      .catch(() => {})
      .finally(() => {
        // Let the refreshed markup settle before allowing the next reconciliation
        setTimeout(() => { giftPending = null; }, 1500);
      });
  };

  /* ---------- Preview only: take the cart icon back from Candy Rack ----------
     With `override_app_cart` on, Candy Rack's slider cart still grabs the header cart
     click and opens its own drawer. Capture the click first and open ours instead. */
  document.addEventListener('click', (evt) => {
    const drawer = document.querySelector('.c-cart[data-c-cart-override]');
    const link = evt.target.closest('.cart-link, a[href$="/cart"]');
    if (!drawer || !link || drawer.contains(link)) return;
    evt.preventDefault();
    evt.stopImmediatePropagation();
    drawer.open(link);
  }, true);

  /* Preview only: Candy Rack's slider cart also calls close() on every theme drawer
     after each cart change (quantity, remove, add). Only honour closes the shopper
     asked for: the close button, the page overlay or Escape. */
  const guardClose = () => {
    const drawer = document.querySelector('.c-cart[data-c-cart-override]');
    if (!drawer || drawer.dataset.closeGuard) return;
    drawer.dataset.closeGuard = 'true';
    let allowUntil = 0;
    const allow = () => { allowUntil = Date.now() + 800; };
    document.addEventListener('click', (evt) => {
      if (evt.target.closest('.js-close-drawer') || evt.target.closest('.js-overlay')) allow();
    }, true);
    document.addEventListener('keydown', (evt) => { if (evt.key === 'Escape') allow(); }, true);
    const originalClose = drawer.close.bind(drawer);
    drawer.close = (...args) => (Date.now() < allowUntil ? originalClose(...args) : undefined);
  };

  /* ---------- Adds made by another app ----------
     Candy Rack's upsell pop-up owns the product page's Add to Cart: it posts to
     /cart/add.js itself and never goes through the theme's form. With its own
     slider cart switched off and its buttons set to "Stay on page", the item
     lands in the cart and nothing on screen says so until the next page load.

     Watch for an add we did not make and open the drawer, so that route ends
     the way the theme's own form does. Guarded on the `open` attribute, so an
     add made from inside the drawer (a pairing card) does not reopen it. Both
     fetch and XHR are wrapped because which one an app uses is its own
     business; each wrapper only observes and hands back the original call. */
  const isCartAdd = (url) => {
    try {
      return /\/cart\/add(\.js)?$/.test(new URL(url, window.location.origin).pathname);
    } catch (err) {
      return false;
    }
  };

  const onExternalAdd = () => {
    const drawer = document.querySelector('.c-cart');
    if (!drawer) return;
    refreshCart();
    if (!drawer.hasAttribute('open')) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
    }
  };

  const nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function wrappedFetch(...args) {
      const url = args[0] && args[0].url ? args[0].url : args[0];
      const result = nativeFetch.apply(this, args);
      if (isCartAdd(url)) {
        result.then((response) => { if (response && response.ok) onExternalAdd(); }).catch(() => {});
      }
      return result;
    };
  }

  const NativeXhrOpen = window.XMLHttpRequest && window.XMLHttpRequest.prototype.open;
  if (NativeXhrOpen) {
    window.XMLHttpRequest.prototype.open = function wrappedOpen(method, url, ...rest) {
      if (String(method).toUpperCase() === 'POST' && isCartAdd(url)) {
        this.addEventListener('load', () => {
          if (this.status >= 200 && this.status < 300) onExternalAdd();
        });
      }
      return NativeXhrOpen.call(this, method, url, ...rest);
    };
  }

  /* ---------- Smile.io launcher ----------
     Smile's "Join VIP" launcher is fixed bottom-right and covers the Checkout button
     while the drawer is open. Mirror the drawer's `open` attribute onto the <html>
     element so the CSS can move it to the other corner on desktop. The stylesheet
     also has a `:has()` rule, so the launcher still moves if this never runs. */
  const watchOpenState = (drawer) => {
    if (drawer.dataset.openWatch) return;
    drawer.dataset.openWatch = 'true';
    const sync = () => {
      document.documentElement.classList.toggle('c-cart-open', drawer.hasAttribute('open'));
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    sync();
  };

  /* ---------- Init on load and after every cart re-render ---------- */
  const init = () => {
    const drawer = document.querySelector('.c-cart');
    if (!drawer) return;
    // Before the empty-cart bail-out below: an empty drawer still opens and closes.
    watchOpenState(drawer);

    const body = drawer.querySelector('[data-c-cart]');
    if (!body) return;
    drawer.classList.add('is-js-ready');
    guardClose();
    loadRecommendations(body);
    reconcileGift(body);
  };

  document.addEventListener('on:cart:after-merge', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
